import React from 'react';
import { Box, Container, Typography, Button, Link } from '@mui/material';
import { StyledGrid as Grid } from '../components/StyledGrid';
import AnimatedSection from '../components/AnimatedSection';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import MapComponent from '../components/Map';
import { styled } from '@mui/system';
import './Gallery.css';
import './HomeHighlightSection.css';
import { inputReviews } from '../data/inputReviews';

const getRandomTestimonials = (count: number) => {
  const shuffled = [...inputReviews].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};

const Home = () => {
  // List all images in public/images/gallery (keep in sync with Gallery.tsx)
  const miniGalleryImages = [
    'IMG_1191.jpg',
    'IMG_1418.jpg',
    'IMG_1563.jpg',
    'IMG_1743.jpg',
    'IMG_1905.jpg',
    'IMG_2974.jpg',
    'IMG_3114.jpg',
    'IMG_3775.jpg',
  ];

  const testimonials = getRandomTestimonials(3);

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Main Heading for SEO */}
      <Typography variant="h1" component="h1" sx={{ display: 'none' }}>
        Stylist Inside Salon – Land O Lakes, FL | Haircuts, Color, and Spa
      </Typography>
      {/* Hero Section */}
      <Box
        sx={{
          height: '50vh',
          background: 'linear-gradient(90deg, #7a4e99 0%, #8e5fb2 50%, #b08ac9 100%)',
          backgroundImage: 'url(/images/content/large_bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(68,68,68,0.5)',
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ color: 'white', textAlign: 'center' }}>
            <AnimatedSection animation="fadeIn" delay={0.2}>
              <Typography variant="h2" component="h2" gutterBottom sx={{ color: 'white' }}>
                Stylist Inside Salon
              </Typography>
            </AnimatedSection>
            <AnimatedSection animation="slideUp" delay={0.6}>
              <Button
                variant="outlined"
                size="large"
                component="a"
                href="https://app.salonrunner.com/customer/directory/index.htm?id=35742"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  mb: 2,
                  backgroundColor: '#b97fc9',
                  borderColor: '#b97fc9', // matches hero gradient mid color
                  color: '#fff',
                  borderWidth: 3,
                  borderStyle: 'solid',
                  boxSizing: 'border-box',
                  fontWeight: 'bold',
                  minWidth: 200,
                  '&:hover, &:active': {
                    backgroundColor: '#b97fc9',
                    borderColor: '#b97fc9',
                    color: '#fff',
                    borderWidth: 3,
                    borderStyle: 'solid',
                    boxSizing: 'border-box',
                  },
                }}
              >
                Make an Appointment Today!
              </Button>
            </AnimatedSection>
          </Box>
        </Container>
      </Box>

      {/* Location Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(270deg, #a06d99 0%, #b97fc9 50%, #cfa2d6 100%)', color: '#432d4f' }}>
        <Container maxWidth="lg">
          <AnimatedSection animation="fadeIn">
            <Typography variant="h2" component="h2" textAlign="center" color="white" gutterBottom sx={{ fontWeight: 'bold', mb: 4, color: 'white' }}>
              Our Location
            </Typography>
            <Grid container spacing={4} alignItems="center">
              {/* Left Side: Text */}
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom sx={{ fontStyle: 'italic', fontWeight: 'bold', color: '#2d1847' }}>
                  Our Location Is
                </Typography>
                <Typography variant="h4" gutterBottom sx={{ fontStyle: 'italic', fontWeight: 'bold', color: '#2d1847' }}>
                    19545 Causeway Blvd, Land O Lakes, FL 34638
                </Typography>
                <Typography variant="h4" gutterBottom sx={{ color: '#432d4f' }}>
                  Full Service Hair Salon and Spa
                </Typography>
                <Typography variant="h4" gutterBottom sx={{ color: '#432d4f' }}>
                  By Appointment or Walk-in's upon Availability
                </Typography>
                <Typography variant="h5" gutterBottom sx={{ mb: 4, color: '#432d4f' }}>
                  If you cannot make your appointment time, please let us know.
                </Typography>
              </Grid>
              {/* Right Side: Map and Contact Buttons */}
              <Grid item xs={12} md={6}>
                <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Link
                    href="https://www.google.com/maps/search/?api=1&query=19545+Causeway+Blvd,+Land+O+Lakes,+FL+34638"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ fontSize: 20, fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <PlaceIcon sx={{ color: 'primary.main' }} />
                    19545 Causeway Blvd, Land O Lakes, FL 34638
                  </Link>
                </Box>
                <Box sx={{ width: '100%', height: { xs: 250, md: 300 }, mb: 3 }}>
                  <MapComponent
                    lat={28.26441}
                    lng={-82.49224}
                    zoom={16}
                    address="19545 Causeway Blvd, Land O Lakes, FL, 34638, United States"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    sx={{
                      backgroundColor: 'transparent',
                      borderColor: '#a06d99', // matches location section gradient start
                      color: '#432d4f',
                      borderWidth: 3,
                      borderStyle: 'solid',
                      boxSizing: 'border-box',
                      fontWeight: 'bold',
                      minWidth: 200,
                      '&:hover, &:active': {
                        backgroundColor: '#a06d99',
                        borderColor: '#a06d99',
                        color: '#fff',
                        borderWidth: 3,
                        borderStyle: 'solid',
                        boxSizing: 'border-box',
                      },
                    }}
                    href="tel:8135265791"
                    startIcon={<PhoneIcon />}
                  >
                    Call for Details
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </AnimatedSection>
        </Container>
      </Box>

      {/* Online Store Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(90deg, #b05c7a 0%, #c96f8e 50%, #e89cae 100%)', color: '#fff'}}>
        <Container maxWidth="lg">
          <AnimatedSection animation="fadeIn">
            <Typography variant="h3" component="h2" textAlign="center" color="white" gutterBottom sx={{ fontWeight: 'bold', mb: 4, color: 'white' }}>
              Try Our New Online Store
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                href="https://shop.saloninteractive.com/store/StylistInside?utm_source=SalonInteractive&utm_medium=web&utm_campaign=ShareMyStore"
                target="_blank"
                startIcon={<ShoppingCartIcon />}
                sx={{
                  backgroundColor: 'transparent',
                  borderColor: '#b05c7a',
                  color: '#fff',
                  borderWidth: 3,
                  borderStyle: 'solid',
                  boxSizing: 'border-box',
                  fontWeight: 'bold',
                  minWidth: 200,
                  '&:hover, &:active': {
                    backgroundColor: '#b05c7a',
                    borderColor: '#b05c7a',
                    color: '#fff',
                    borderWidth: 3,
                    borderStyle: 'solid',
                    boxSizing: 'border-box',
                  },
                }}
              >
                Online Store
              </Button>
            </Box>
          </AnimatedSection>
        </Container>
      </Box>

      {/* Stylist Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(270deg, #a07c7a 0%, #b89c95 50%, #c7b1ad 100%)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="/images/team/tabitha-anton.png"
                alt="Tabitha (Anton) Spedale"
                sx={{
                  width: '75%',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: 3,
                  border: '6px solid #fff',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <AnimatedSection animation="fadeIn">
                <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#3a2323' }}>
                  Meet Tabitha (Anton) Spedale
                </Typography>
                <Typography variant="h4" gutterBottom sx={{ color: '#432d4f', mb: 4 }}>
                  Owner & Master Stylist / Colorist
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    href="/prices"
                    fullWidth
                    sx={{
                      backgroundColor: 'transparent',
                      borderColor: '#a07c7a', // stylist section gradient start
                      color: '#432d4f',
                      borderWidth: 3,
                      borderStyle: 'solid',
                      boxSizing: 'border-box',
                      fontWeight: 'bold',
                      minWidth: 200,
                      '&:hover, &:active': {
                        backgroundColor: '#a07c7a',
                        borderColor: '#a07c7a',
                        color: '#fff',
                        borderWidth: 3,
                        borderStyle: 'solid',
                        boxSizing: 'border-box',
                      },
                    }}
                  >
                    Services and Pricing
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    href="https://app.salonrunner.com/customer/directory/index.htm?id=35742"
                    target="_blank"
                    fullWidth
                    sx={{
                      backgroundColor: 'transparent',
                      borderColor: '#a07c7a',
                      color: '#432d4f',
                      borderWidth: 3,
                      borderStyle: 'solid',
                      boxSizing: 'border-box',
                      fontWeight: 'bold',
                      minWidth: 200,
                      '&:hover, &:active': {
                        backgroundColor: '#a07c7a',
                        borderColor: '#a07c7a',
                        color: '#fff',
                        borderWidth: 3,
                        borderStyle: 'solid',
                        boxSizing: 'border-box',
                      },
                    }}
                  >
                    Book with Tabitha
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    href="tel:8135916538"
                    fullWidth
                    startIcon={<PhoneIcon />}
                    sx={{
                      backgroundColor: 'transparent',
                      borderColor: '#a07c7a',
                      color: '#432d4f',
                      borderWidth: 3,
                      borderStyle: 'solid',
                      boxSizing: 'border-box',
                      fontWeight: 'bold',
                      minWidth: 200,
                      '&:hover, &:active': {
                        backgroundColor: '#a07c7a',
                        borderColor: '#a07c7a',
                        color: '#fff',
                        borderWidth: 3,
                        borderStyle: 'solid',
                        boxSizing: 'border-box',
                      },
                    }}
                  >
                    Call or Text Tabitha
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    href="mailto:tabithaanton@stylistinside.com"
                    fullWidth
                    startIcon={<EmailIcon />}
                    sx={{
                      backgroundColor: 'transparent',
                      borderColor: '#a07c7a',
                      color: '#432d4f',
                      borderWidth: 3,
                      borderStyle: 'solid',
                      boxSizing: 'border-box',
                      fontWeight: 'bold',
                      minWidth: 200,
                      '&:hover, &:active': {
                        backgroundColor: '#a07c7a',
                        borderColor: '#a07c7a',
                        color: '#fff',
                        borderWidth: 3,
                        borderStyle: 'solid',
                        boxSizing: 'border-box',
                      },
                    }}
                  >
                    Email Tabitha
                  </Button>
                </Box>
              </AnimatedSection>
            </Grid>
          </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button 
                    variant="outlined"
                    href="/team"
                    sx={{
                      backgroundColor: 'transparent',
                      borderColor: '#a07c7a',
                      color: '#432d4f',
                      borderWidth: 3,
                      borderStyle: 'solid',
                      boxSizing: 'border-box',
                      fontWeight: 'bold',
                      minWidth: 200,
                      '&:hover, &:active': {
                        backgroundColor: '#a07c7a',
                        borderColor: '#a07c7a',
                        color: '#fff',
                        borderWidth: 3,
                        borderStyle: 'solid',
                        boxSizing: 'border-box',
                      },
                    }}
                  >
                    Meet Our Team
                  </Button>
                </Box>
        </Container>
      </Box>
      {/* Highlight Section: Gallery + Testimonials */}
      <div className="home-highlight-section" style={{ background: 'linear-gradient(90deg, #b97fc9 0%, #d48eea 50%, #cfa2d6 100%)', padding: '64px 0', color: 'white' }}>
        <div className="home-gallery-preview">
          <Typography variant="h4" align="center" gutterBottom sx={{ mb: 2, color: 'white' }}>
            Gallery
          </Typography>
          <div className="gallery-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, maxWidth: 520, margin: '0 auto' }}>
            {miniGalleryImages.slice(0, 4).map((filename) => (
              <div key={filename} className="gallery-img-container" style={{ height: 250 }}>
                <img
                  src={`/images/gallery/${filename}`}
                  alt={filename}
                  className="gallery-img"
                  style={{ height: '100%', objectFit: 'cover' }}
                  loading="lazy"
                />
                <div className="sheen" />
              </div>
            ))}
          </div>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button 
              variant="outlined"
              href="/gallery"
            >
              View Full Gallery
            </Button>
          </Box>
        </div>
        <div className="home-testimonials">
          <Typography variant="h4" align="center" gutterBottom sx={{ color: 'white' }}>
            Testimonials
          </Typography>
          {testimonials.map((review, idx) => (
            <div className="testimonial" key={review.author + idx}>
              <div className="testimonial-author">{review.author}</div>
              <div className="testimonial-quote">{review.text}</div>
              <div className="testimonial-source">via Facebook · {new Date(review.date).getFullYear() === new Date().getFullYear() ? 'this year' : `${new Date().getFullYear() - new Date(review.date).getFullYear()} years ago`}</div>
            </div>
          ))}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button 
              variant="outlined"
              href="/reviews"
            >
              See More Reviews
            </Button>
            <Button 
              variant="outlined"
              href="/contact"
              sx={{ ml: 2 }}
            >
              Contact Us
            </Button>
          </Box>
        </div>
      </div>
    </Box>
  );
};

export default Home;
